import React, { useEffect, useState } from "react";

import { FieldPath, FieldValues, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@mui/material";
import Autocomplete, { AutocompleteProps, createFilterOptions } from "@mui/material/Autocomplete";
import TextField, { TextFieldProps as TextFieldPropsMUI } from "@mui/material/TextField";

import { CloseLightBlue, RafterDownIcon } from "@/assets/icons";
import { IdentifiableTypes, IdNamePair } from "@/shared/domain";

import ControllerInput, { ControllerInputProps } from "../ControllerInput/ControllerInput";

type OptionType = IdNamePair<IdentifiableTypes> & { inputValue?: string };

export type SelectInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> &
    Omit<AutocompleteProps<IdNamePair<IdentifiableTypes>, boolean, boolean, boolean>, "options" | "renderInput"> & {
        options: (IdentifiableTypes | OptionType)[];
        TextFieldProps?: TextFieldPropsMUI;
        withEmptyOption?: boolean;
        nullable?: boolean;
    };

const emptyOption = { id: "", name: "-", preferred: false };

// Display conditions:
// - Options available regardless the loading status: display options
// - Loading & no options: show loading text
// - Not loading & no options: show empty text

const SelectInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    options,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    hideErrorMessage,
    label,
    fullWidth,
    required = false,
    freeSolo,
    TextFieldProps,
    disableClearable,
    multiple,
    withEmptyOption,
    nullable = false,
    loading,
    ...autoCompleteFieldProps
}: SelectInputProps<TFieldValues, TName>): React.ReactElement => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const filter = createFilterOptions<OptionType>();

    const { field } = useController({ name, control });

    let allOptions: OptionType[] = (options || [])
        .map((option) =>
            typeof option !== "object" ? { id: option, name: option.toString(), preferred: false } : option,
        )
        .sort((optionA, optionB) => optionA.name.toString().localeCompare(optionB.name.toString()));

    const preferredOptions = allOptions.filter((option) => option.preferred);

    allOptions = allOptions.map((option) => ({ ...option, preferred: false }));

    // Only add the empty option if it is not loading, otherwise it will be added in the list of options, and it will be shown as loaded.
    withEmptyOption && !loading && allOptions.unshift(emptyOption);

    // when it has preferred options duplicate these options then it shows it in the suggested group and in the all group
    allOptions = [...preferredOptions, ...allOptions];

    const hasPreferred = preferredOptions.length > 0;

    const findOption = (optionId: IdentifiableTypes) => allOptions.find((option) => option.id === optionId);

    const emptyValue = nullable ? null : "";

    useEffect(() => {
        // If the value is not in the options, then it is cleared (only if it is not freeSolo, and it is not multiple)
        if (
            !freeSolo &&
            field.value &&
            !Array.isArray(field.value) &&
            options?.length &&
            !allOptions.some((option) => option.id === field.value)
        ) {
            field.onChange("");
        }
    }, [field.value, options]);

    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={LabelProps}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            render={({ field: { onChange, onBlur, value, ref }, error }) => {
                return (
                    <Autocomplete<OptionType, boolean, boolean, boolean>
                        autoHighlight
                        open={open}
                        id={name}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        options={allOptions}
                        // when the value is empty and the withEmptyOption is true, the empty label (this label: "-") is hidden if selected.
                        // but if there is an option with empty id, it is shown when selected.
                        value={withEmptyOption ? value || emptyValue : value ?? emptyValue}
                        onBlur={onBlur}
                        multiple={multiple}
                        groupBy={(option) => {
                            if (hasPreferred) {
                                return option.preferred ? t("Common.Suggested") : t("Common.All");
                            }
                            return "";
                        }}
                        disableCloseOnSelect={multiple}
                        disableClearable={required || disableClearable || withEmptyOption}
                        ChipProps={{
                            deleteIcon: <CloseLightBlue />,
                        }}
                        onChange={(_event, newValue, reason) => {
                            if (Array.isArray(newValue)) {
                                onChange(newValue.map((e) => (typeof e === "object" ? e.id : e)));
                            } else {
                                if (reason === "clear" || !newValue || newValue === emptyOption) {
                                    onChange(emptyValue);
                                } else if (typeof newValue !== "object") {
                                    onChange(newValue);
                                } else {
                                    onChange(newValue?.inputValue || newValue.id);
                                }
                            }
                        }}
                        // Added the optional chain to fix when the id is a null
                        // @ts-ignore
                        isOptionEqualToValue={(option, testValue) =>
                            value === "" ||
                            option.id?.toString?.().toLowerCase() === testValue?.toString?.().toLowerCase()
                        }
                        filterOptions={(filterOptions, params) => {
                            let filtered = filter(filterOptions, params);

                            if (params.inputValue !== "" && params.inputValue !== value) {
                                filtered = filtered.filter((option) => !option.preferred);
                            }

                            // Suggest the creation of a new value
                            if (freeSolo && params.inputValue !== "" && !findOption(params.inputValue)) {
                                filtered.push({
                                    id: params.inputValue,
                                    inputValue: params.inputValue,
                                    preferred: false,
                                    name: t("Common.UseValue", { value: params.inputValue }),
                                });
                            }

                            return filtered;
                        }}
                        selectOnFocus
                        clearOnBlur
                        renderOption={(props, option, { selected }) => {
                            return (
                                <li
                                    {...props}
                                    data-testid={`${name}-option-${option.id}`}
                                    key={`${name}-option-${option.id}-${option.name}`}
                                >
                                    {multiple && <Checkbox checked={selected} />}
                                    {option.name}
                                </li>
                            );
                        }}
                        handleHomeEndKeys
                        forcePopupIcon
                        freeSolo={freeSolo}
                        loading={loading}
                        popupIcon={<RafterDownIcon />}
                        getOptionLabel={(option) => {
                            if (typeof option !== "object") {
                                return findOption(option)?.name || option;
                            } else {
                                return option.name;
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={!!error}
                                inputRef={ref}
                                name={name}
                                {...TextFieldProps}
                                inputProps={{
                                    ...params.inputProps,
                                    ...TextFieldProps?.inputProps,
                                    autoComplete: "no",
                                    "data-testid": `select_input-${name}`,
                                }}
                            />
                        )}
                        {...autoCompleteFieldProps}
                    />
                );
            }}
        />
    );
};

export default SelectInput;
